import React from "react";
import {
  Float,
  Image as RPFCImage,
  View,
} from "@surge-global-engineering/react-pdf-components";
import { PdfSlateBaseParentBlockNode, PdfSlateEndnote, PdfSlateImage, PdfSlateLink, PdfSlateListBlock, PdfSlateProfileLinks, PdfSlateTextNode, PdfTheme } from "../types";

export type SurroundingNodeTypes = PdfSlateBaseParentBlockNode | PdfSlateTextNode | PdfSlateListBlock | PdfSlateEndnote | PdfSlateImage | PdfSlateLink | PdfSlateProfileLinks;
interface ImageProps {
  imageNode: PdfSlateImage;
  theme: PdfTheme;
  lightTextCaption?: boolean;
  priorNode?: SurroundingNodeTypes;
  previousNode?: SurroundingNodeTypes;
  nextNode?: SurroundingNodeTypes;
  thenNode?: SurroundingNodeTypes;
}
const Image: React.FC<ImageProps> = ({ theme, imageNode, lightTextCaption = false, priorNode, previousNode, nextNode, thenNode }) => {
  const { printBaseFont, baseFontSize } = theme.properties;
  const {
    caption: imageCaption,
    flow,
    size,
    url,
    link,
    wrap,
    separatePage,
  } = imageNode;

  const alignment: "center" | "left" | "right" =
    flow === "middle" ? "center" : flow;

  const caption = imageCaption ? imageCaption : undefined;

  const getSizes = (): number => {
    if (typeof size === "string") {
      if (size === "small") return 30;
      if (size === "medium") return 45;
      if (size === "large") return 100;
    }
    return size;
  };

  const getImageContainerStyles = () => {
    let paddingRight = 0;
    let paddingLeft = 0;
    if (flow === "left") paddingRight = 7;
    if (flow === "right") paddingLeft = 7;
    return { paddingBottom: 7, paddingTop: 4, paddingRight, paddingLeft };
  };

  const props = {
    src: url,
    caption,
    width: getSizes(),
    alignment,
    link: link?.webLink,
    captionTextStyles: {
      fontFamily: printBaseFont,
      fontSize: baseFontSize,
      color: lightTextCaption ? "#ffffff" : "#000000"
    },
    imageContainerStyle: getImageContainerStyles(),
  };

   // text wrapping, in-chapter image
   if (wrap && !separatePage) {
    return (
      <>
        <Float direction={flow as "left" | "right"}>
          <RPFCImage {...props} />
        </Float>
      </>
    );
  }

  // not wrapping, in-chapter image
  if(!wrap && !separatePage) {
    return (<RPFCImage {...props} />);
  }

  /***
   * Rendering the separatePage image
   * 
   * Checking for two possible instances of the surrounding nodes since usually there's an emtpy "p" node between the elements or,
   * sometimes the empty "p" node is unavailable if the element is added while focusing inline.
   * 
  ***/

  // separatePage image followed by another separatePage image
  const isNextNodeSeparatePageImage = ((nextNode as PdfSlateImage)?.type === "image" && (nextNode as PdfSlateImage)?.separatePage);
  const isNextNodeAfterAnEmptyNodeSeparatePageImage  = ((nextNode as PdfSlateBaseParentBlockNode)?.type === "p" && (nextNode as any ).children[0].text === "" && (thenNode as PdfSlateImage)?.type === "image" && (thenNode as PdfSlateImage)?.separatePage);

  if (isNextNodeSeparatePageImage || isNextNodeAfterAnEmptyNodeSeparatePageImage) {
    return (
      <>
        <View break/>
        <RPFCImage {...props} />
      </>
    );
  }

  // separatePage image immediately after a page-break 
  const isPreviousNodePageBreak = ((previousNode as PdfSlateBaseParentBlockNode)?.type === "page-break");
  const isPreviousNodeBeforeAnEmptyNodePageBreak = ((previousNode as PdfSlateBaseParentBlockNode)?.type === "p" && (previousNode as any)?.children[0].text === "" && (priorNode as PdfSlateBaseParentBlockNode)?.type === "page-break");

  if (isPreviousNodePageBreak || isPreviousNodeBeforeAnEmptyNodePageBreak) {
    return (<RPFCImage {...props} />);
  }

  //separatePage image immediately before a page-break
  const isNextNodePageBreak = ((nextNode as PdfSlateBaseParentBlockNode)?.type === "page-break");
  const isNextNodeAfterAnEmptyNodePageBreak = ((nextNode as PdfSlateBaseParentBlockNode)?.type === "p" && (nextNode as any)?.children[0].text === "" && (thenNode as PdfSlateBaseParentBlockNode)?.type === "page-break");

  if (isNextNodePageBreak || isNextNodeAfterAnEmptyNodePageBreak) {
    return (
      <>
        <View break/>
        <RPFCImage {...props} />
      </>
    );
  }

  //separatePage image is at the end of the chapter
  if (nextNode === undefined && thenNode === undefined) {
    return (
      <>
        <View break/>
        <RPFCImage {...props} />
      </>
    );
  }

  // non-breaking separatePage image
  return (
    <>
      {separatePage && <View break/>}
      <RPFCImage {...props} />
      {separatePage && <View break/>}
    </>
  );
};
export default Image;
